import React from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "~/components/modal/modal";
import { IUser } from "~/integrations/firebase/collections";
import { Text } from "~/components/text";
import { ProfilePicture } from "~/components/user/profile-picture";
import { useOrganisations } from "~/contexts/organisations-provider";

interface FormValues {
  users: {
    email: string;
    role: string;
  }[];
}

interface TransferUsersConfirmationDialogProps {
  open: boolean;
  existingUsersToConfirm: IUser[];
  formValues: FormValues | null;
  isSubmitting: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export const TransferUsersConfirmationDialog: React.FC<
  TransferUsersConfirmationDialogProps
> = ({
  open,
  existingUsersToConfirm,
  formValues,
  isSubmitting,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation(["settings"]);
  const { organisations } = useOrganisations();
  const organisationNamesById = new Map(
    organisations.map((organisation) => [organisation.id, organisation.name]),
  );

  return (
    <Modal
      id="transfer-users"
      open={open}
      variant="warning"
      title={t("settings:organisation.members.transfer.title")}
      description={t("settings:organisation.members.transfer.description", {
        count: existingUsersToConfirm.length,
      })}
      confirmLabel={t("settings:organisation.members.transfer.confirm")}
      cancelLabel={t("settings:organisation.members.transfer.cancel")}
      confirmIconName={isSubmitting ? "loader" : undefined}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onClose();
        }
      }}
      onConfirm={onConfirm}
      disabledConfirm={isSubmitting}
    >
      <div className="my-4 flex flex-col gap-2">
        {formValues && (
          <div className="flex items-center justify-between bg-secondary-50 p-3 rounded-md border border-secondary-200">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-system-success rounded-full" />
              <Text as="span" variant="label">
                {t("settings:organisation.members.transfer.newUsers")}
              </Text>
            </div>
            <Text as="span" variant="bodyStrong">
              {
                formValues.users.filter(
                  (user) =>
                    !existingUsersToConfirm.some(
                      (existingUser) => existingUser.email === user.email,
                    ),
                ).length
              }
            </Text>
          </div>
        )}
        <div className="flex items-center justify-between bg-secondary-50 p-3 rounded-md border border-secondary-200">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-system-warning rounded-full" />
            <Text as="span" variant="label">
              {t("settings:organisation.members.transfer.usersToTransfer")}
            </Text>
          </div>
          <Text as="span" variant="bodyStrong">
            {existingUsersToConfirm.length}
          </Text>
        </div>
      </div>

      <div className="mb-6">
        <Text as="h4" variant="label" className="mb-3">
          {t("settings:organisation.members.transfer.usersToBeTransferred")}
        </Text>
        <div className="max-h-[160px] overflow-y-auto border border-secondary-200 rounded-md p-2 bg-secondary-50">
          <ul className="text-sm text-secondary-600 space-y-2">
            {existingUsersToConfirm.map((user) => {
              const organisationName =
                user.organisation &&
                organisationNamesById.get(user.organisation.id);

              return (
                <li
                  key={user.id}
                  className="flex items-center p-1 rounded transition-colors"
                >
                  <ProfilePicture
                    name={`${user.givenName} ${user.familyName}`}
                    className="w-8 h-8 mr-3"
                    avatarClassName="bg-primary-100 text-primary-700"
                  />
                  <div className="overflow-hidden">
                    <Text as="div" variant="label" className="truncate">
                      {user.email}
                    </Text>
                    <Text as="div" variant="caption">
                      {organisationName ||
                        t(
                          "settings:organisation.members.transfer.unknownOrganisation",
                        )}
                    </Text>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </Modal>
  );
};
